import { useCallback } from 'react';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { addNavPin, listMyNavPins, removeNavPin, type NavPin } from '@pc/db';

const NAV_PINS_KEY = ['shell', 'nav-pins'] as const;

/**
 * The signed-in user's pinned-nav state + a toggle. Pins live in
 * `core.user_nav_pins` (RLS-scoped to auth.uid()), so they follow the user
 * across every workspace and device.
 *
 * Both mutations apply an optimistic cache write so the star and the
 * "Pinned" strip update on the same frame as the click; the list query
 * reconciles on settle and rolls back on error.
 */
export function useNavPins(enabled: boolean) {
  const qc = useQueryClient();
  const pinsQ = useQuery({
    queryKey: NAV_PINS_KEY,
    queryFn: listMyNavPins,
    enabled,
    staleTime: 60_000,
  });

  const addM = useMutation({
    mutationFn: addNavPin,
    onMutate: async ({ toPath, label }) => {
      await qc.cancelQueries({ queryKey: NAV_PINS_KEY });
      const prev = qc.getQueryData<NavPin[]>(NAV_PINS_KEY) ?? [];
      if (!prev.some((p) => p.to_path === toPath)) {
        const optimistic: NavPin = {
          to_path: toPath,
          label,
          sort_order: Math.floor(Date.now() / 1000),
          created_at: new Date().toISOString(),
        };
        qc.setQueryData<NavPin[]>(NAV_PINS_KEY, [...prev, optimistic]);
      }
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(NAV_PINS_KEY, ctx.prev);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: NAV_PINS_KEY }),
  });

  const removeM = useMutation({
    mutationFn: removeNavPin,
    onMutate: async (toPath: string) => {
      await qc.cancelQueries({ queryKey: NAV_PINS_KEY });
      const prev = qc.getQueryData<NavPin[]>(NAV_PINS_KEY) ?? [];
      qc.setQueryData<NavPin[]>(
        NAV_PINS_KEY,
        prev.filter((p) => p.to_path !== toPath),
      );
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(NAV_PINS_KEY, ctx.prev);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: NAV_PINS_KEY }),
  });

  const pins = pinsQ.data ?? [];
  const pinnedPaths = new Set(pins.map((p) => p.to_path));

  const toggle = useCallback(
    (toPath: string, label: string, currentlyPinned: boolean) => {
      if (currentlyPinned) removeM.mutate(toPath);
      else addM.mutate({ toPath, label });
    },
    [addM, removeM],
  );

  return {
    pins,
    isPinned: (toPath: string) => pinnedPaths.has(toPath),
    toggle,
  };
}

function StarIcon({ filled }: { filled: boolean }) {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill={filled ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M12 2.5l2.9 5.88 6.49.94-4.7 4.58 1.11 6.46L12 17.8l-5.8 3.05 1.1-6.46-4.69-4.58 6.49-.94L12 2.5z" />
    </svg>
  );
}

/**
 * A sidebar nav row: the destination link plus a pin/unpin star. The star
 * is filled + full-strength when pinned. When unpinned it sits faint but
 * always visible (so the feature is discoverable AND tappable on touch,
 * where there is no hover) and brightens on row-hover / focus.
 */
export function NavRow({
  to,
  end,
  label,
  pinned,
  onTogglePin,
}: {
  to: string;
  end?: boolean;
  label: string;
  pinned: boolean;
  onTogglePin: () => void;
}) {
  const { t } = useTranslation();
  const pinTitle = pinned
    ? t('app.shell.unpinFromTop', { label })
    : t('app.shell.pinToTop', { label });
  return (
    <div className="group flex items-center gap-1">
      <NavLink
        to={to}
        end={end}
        className={({ isActive }) =>
          `flex-1 min-w-0 truncate py-2 text-sm ${
            isActive ? 'text-ink' : 'text-muted hover:text-ink'
          }`
        }
      >
        {label}
      </NavLink>
      <button
        type="button"
        aria-label={pinTitle}
        aria-pressed={pinned}
        title={pinTitle}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onTogglePin();
        }}
        className={`shrink-0 rounded p-1.5 outline-none transition-colors focus-visible:text-ink ${
          pinned
            ? 'text-ink'
            : 'text-muted/30 group-hover:text-muted hover:!text-ink'
        }`}
      >
        <StarIcon filled={pinned} />
      </button>
    </div>
  );
}
